import { createColumnHelper } from '@tanstack/react-table';
import { differenceInYears, isValid } from 'date-fns';
import React, { useMemo, useState } from 'react';
import { LuArrowRightLeft } from 'react-icons/lu';

import { InviteStatus, Role } from '@ndvr/auth-api';
import { GlobalAccountRoleType, PersonOrgFinancialProfile, PersonOrgProfile, PersonOrgType } from '@ndvr/customer-api';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { DropdownMenuItem, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { InviteButton, PersonDrawer } from 'modules';
import { formatDate, getGlobalAccountRoleDisplayLabel, getPersonName, parseLocalDate, useRequiredParams } from 'utils';
import { useUserHasRole } from 'views/admin/dev-queries';
import { useInviteDetails } from 'views/person/person-queries';
import {
    CondensedTable,
    FormModal,
    HorizontalLayout,
    MailTo,
    ModalText,
    PhoneNumber,
    Spinner,
    Text,
    TextLink,
    VerticalLayout,
} from 'widgets';

import { useAddPersonToHouseholdWithRole, usePersonOrgLastLoginsForHousehold } from '../household-queries';
import { LinkPersonToHouseholdRoleFields, linkPersonToHouseholdRoleFormSchema } from '../LinkPersonToHouseholdDrawer';
import { MemberActionsCell, useMemberLinkActions } from './MemberLinkActions';
import { TaxIdCell } from './TaxIdCell';

const columnHelper = createColumnHelper<PersonOrgFinancialProfile>();
const changeRoleFormSchema = linkPersonToHouseholdRoleFormSchema;

export const PersonsList = ({ profiles }: { profiles: PersonOrgFinancialProfile[] }) => {
    const { globalAccountGUID } = useRequiredParams(['globalAccountGUID']);
    const { data: ssnAccess } = useUserHasRole(Role.SSN_ACCESS);
    const { mutate: addPersonToHouseholdWithRole } = useAddPersonToHouseholdWithRole();
    const { data: lastLogins } = usePersonOrgLastLoginsForHousehold(globalAccountGUID);
    const persons = useMemo(
        () => profiles.filter(({ profile }) => profile.personOrg.personOrgType === PersonOrgType.PERSON),
        [profiles],
    );

    const { openLinkDrawer, requestUnlink, linkUnlinkNodes } = useMemberLinkActions({
        personOrgType: PersonOrgType.PERSON,
        unlinkNoun: 'person',
        getDisplayName: ({ profile }) => getPersonName(profile.personOrg),
        unlinkNote: ({ profile }) => <ModalText>{profile.personOrg.primaryEmail?.plainText}</ModalText>,
    });
    const [personToChangeRole, setPersonToChangeRole] = useState<PersonOrgFinancialProfile | undefined>(undefined);

    const [showPersonCreateDrawer, setShowPersonCreateDrawer] = useState(false);
    const [personToEdit, setPersonToEdit] = useState<PersonOrgFinancialProfile | undefined>(undefined);

    const onSubmitRoleChange = (data: { accountRoleType?: string; accountRoleLabel?: string }) => {
        if (!personToChangeRole) {
            return;
        }

        const roleLabel = data.accountRoleType === 'Custom' ? data.accountRoleLabel : data.accountRoleType;

        addPersonToHouseholdWithRole(
            {
                globalAccountGUID,
                personOrgGUID: personToChangeRole.profile.personOrgGUID,
                roleType: GlobalAccountRoleType.SECONDARY_USER,
                roleLabel,
            },
            {
                onSuccess: () => setPersonToChangeRole(undefined),
            },
        );
    };

    const columns = [
        columnHelper.accessor(({ profile }) => getPersonName(profile.personOrg), {
            id: 'name',
            header: 'Name',
            cell: ({ getValue, row }) => {
                const role = row.original.profile.globalAccountPersonOrgRoles?.[0];

                return (
                    <VerticalLayout>
                        <TextLink to={`/person/${row.original.profile.personOrgGUID}`}>{getValue()}</TextLink>
                        <Text>{getGlobalAccountRoleDisplayLabel(role)}</Text>
                    </VerticalLayout>
                );
            },
        }),
        columnHelper.accessor(({ profile }) => profile.personOrg.primaryEmail?.plainText, {
            id: 'contact',
            header: 'Contact',
            cell: ({ getValue, row }) => {
                return (
                    <VerticalLayout>
                        <MailTo address={getValue()} />
                        <PhoneNumber phoneNumber={row.original.profile.personOrg.personalPhone?.plainText} />
                    </VerticalLayout>
                );
            },
        }),
        columnHelper.accessor(({ profile }) => profile.personOrg.dateOfBirth?.plainText, {
            id: 'dateOfBirth',
            header: 'Date of Birth',
            cell: ({ getValue }) => <Text>{formatDate(getValue())}</Text>,
        }),
        columnHelper.display({
            id: 'age',
            header: 'Age',
            cell: ({ row }) => {
                const dateOfBirth = row.original.profile.personOrg.dateOfBirth?.plainText;
                if (!dateOfBirth) return <Text />;

                try {
                    const parsedDate = parseLocalDate(dateOfBirth);
                    if (!isValid(parsedDate)) return <Text />;
                    const age = differenceInYears(new Date(), parsedDate);
                    return <Text>{age}</Text>;
                } catch {
                    return <Text />;
                }
            },
            size: 80,
        }),
        ...(ssnAccess
            ? [
                  columnHelper.display({
                      id: 'taxID',
                      header: 'Tax ID',
                      cell: ({ row }) => (
                          <TaxIdCell
                              key={row.original.profile.personOrg.personOrgGUID}
                              personOrg={row.original.profile.personOrg}
                          />
                      ),
                      size: 160,
                  }),
              ]
            : []),
        columnHelper.display({
            id: 'loginState',
            header: 'Login State',
            cell: ({ row }) => (
                <div onClick={(event) => event.stopPropagation()} onMouseDown={(event) => event.stopPropagation()}>
                    <LoginStateDisplay
                        personProfile={row.original.profile}
                        lastLoginAt={lastLogins?.get(row.original.profile.personOrgGUID)}
                    />
                </div>
            ),
            size: 170,
        }),
        columnHelper.display({
            id: 'actions',
            cell: ({ row }) => {
                const role = row.original.profile.globalAccountPersonOrgRoles?.[0];
                const isPrimaryAccountManager = role?.roleType === GlobalAccountRoleType.PRIMARY_ACCOUNT_MANAGER;
                return (
                    <MemberActionsCell
                        profile={row.original}
                        onEdit={setPersonToEdit}
                        onUnlink={requestUnlink}
                        unlinkDisabled={isPrimaryAccountManager}
                        extraMenuItems={
                            <>
                                <DropdownMenuItem
                                    onSelect={() => setPersonToChangeRole(row.original)}
                                    disabled={isPrimaryAccountManager}
                                    className="flex w-full items-center gap-2"
                                >
                                    <LuArrowRightLeft className="h-4 w-4" />
                                    Change role
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                            </>
                        }
                    />
                );
            },
        }),
    ];

    return (
        <VerticalLayout $gap={8}>
            <HorizontalLayout $apart>
                <h2 className="text-cocoa-1000 text-xl">Persons</h2>
                <HorizontalLayout $gap={16}>
                    <Button onClick={openLinkDrawer}>Link Person</Button>
                    <Button onClick={() => setShowPersonCreateDrawer(true)}>New Person</Button>
                </HorizontalLayout>
            </HorizontalLayout>
            <Card>
                <CondensedTable data={persons} columns={columns} onRowClick={setPersonToEdit} />
            </Card>

            {linkUnlinkNodes}
            {showPersonCreateDrawer && (
                <PersonDrawer
                    onClose={() => setShowPersonCreateDrawer(false)}
                    mode="create-household"
                    globalAccountGUID={globalAccountGUID}
                />
            )}
            {personToEdit && (
                <PersonDrawer
                    onClose={() => setPersonToEdit(undefined)}
                    mode="edit"
                    personGUID={personToEdit.profile.personOrgGUID}
                    invalidateGlobalAccountGUID={globalAccountGUID}
                />
            )}
            {personToChangeRole && (
                <FormModal
                    modalTitle={getPersonName(personToChangeRole.profile.personOrg)}
                    onClose={() => setPersonToChangeRole(undefined)}
                    onSubmit={onSubmitRoleChange}
                    formSchema={changeRoleFormSchema}
                    formDefaultValues={getRoleFormDefaultValues(personToChangeRole)}
                    submitButtonText="Save"
                >
                    <LinkPersonToHouseholdRoleFields />
                </FormModal>
            )}
        </VerticalLayout>
    );
};

const getRoleFormDefaultValues = (person: PersonOrgFinancialProfile) => {
    const role = person.profile.globalAccountPersonOrgRoles?.[0];
    const roleLabel = role?.roleLabel ?? '';
    const predefinedRoleOptions = new Set(['Client', 'Family', 'Lawyer', 'Tax Advisor']);
    const accountRoleType = roleLabel && predefinedRoleOptions.has(roleLabel) ? roleLabel : roleLabel ? 'Custom' : '';

    return {
        accountRoleType,
        accountRoleLabel: accountRoleType === 'Custom' ? roleLabel : '',
    };
};

const LoginStateDisplay = ({
    personProfile,
    lastLoginAt,
}: {
    personProfile: PersonOrgProfile;
    lastLoginAt?: string;
}) => {
    const { data: inviteDetails, isLoading } = useInviteDetails(personProfile.personOrgGUID);

    if (isLoading) {
        return <Spinner />;
    }

    return inviteDetails?.inviteStatus === InviteStatus.REGISTERED ? (
        <VerticalLayout>
            <Text>{formatDate(lastLoginAt)}</Text>
            <Text $themeColor="subtle">last login</Text>
        </VerticalLayout>
    ) : inviteDetails ? (
        <InviteButton personOrgProfile={personProfile} inviteDetails={inviteDetails} />
    ) : null;
};
